import { defineMessages } from 'react-intl';

const messages = defineMessages({
    accessTypeTitle: {
        defaultMessage: 'ACCESS TYPE',
        description: 'Title for "Access Type" menu, in all capital letters',
        id: 'boxui.share.accessType',
    },
    canEdit: {
        defaultMessage: 'Can edit',
        description: 'Label for a shared link permission level',
        id: 'boxui.share.canEdit',
    },
    canView: {
        defaultMessage: 'Can view',
        description: 'Label for a shared link permission level',
        id: 'boxui.share.canView',
    },
    enterAtLeastOneEmailError: {
        defaultMessage: 'Enter at least one valid email',
        description: 'Error message when user tries to send shared link as email without entering any recipients',
        id: 'boxui.share.enterAtLeastOneEmail',
    },
    emailSharedLink: {
        defaultMessage: 'Email Shared Link',
        description: 'Field label for shared link recipient list (title-case)',
        id: 'boxui.share.emailSharedLink',
    },
    removeLink: {
        defaultMessage: 'Remove Link',
        description: 'Label for option to remove shared link',
        id: 'boxui.share.removeLink',
    },
    settingsButtonLabel: {
        defaultMessage: 'Open shared link settings popup',
        description: 'Accessible label for button that loads share settings popup',
        id: 'boxui.share.settingsButtonLabel',
    },
    sharedLinkLabel: {
        defaultMessage: 'Shared Link',
        description: 'Label for field to copy shared link URL (title-case)',
        id: 'boxui.share.sharedLinkLabel',
    },
    sharedLinkModalTitle: {
        defaultMessage: 'Shared Link for {itemName}',
        description: 'Title for shared link modal (title-case)',
        id: 'boxui.share.sharedLinkModalTitle',
    },
    messageTitle: {
        defaultMessage: 'Message',
        description: 'Label for "Message" text box to email the shared link (title-case)',
        id: 'boxui.share.message',
    },
    peopleWithTheLink: {
        defaultMessage: 'People with the link',
        description: 'Label for "People with the link" option',
        id: 'boxui.share.peopleWithTheLink',
    },
    peopleInEnterprise: {
        defaultMessage: 'People in {enterpriseName}',
        description:
            'This string describes the access level of a file or folder, or who can see the item. {enterpriseName} is the company name',
        id: 'boxui.share.peopleInEnterprise',
    },
    peopleInYourCompany: {
        defaultMessage: 'People in your company',
        description: 'Label for "People in your company" option',
        id: 'boxui.share.peopleInYourCompany',
    },
    peopleInThisFolder: {
        defaultMessage: 'People in this folder',
        description: 'Label for "People in this folder" option',
        id: 'boxui.share.peopleInThisFolder',
    },
    peopleInThisFile: {
        defaultMessage: 'People in this file',
        description: 'Label for "People in this file" option',
        id: 'boxui.share.peopleInThisFile',
    },
    removeLinkConfirmationTitle: {
        defaultMessage: 'Remove Shared Link',
        description: 'Label for confirmation modal to remove a shared link (title-case)',
        id: 'boxui.share.removeLinkConfirmationTitle',
    },
    removeLinkConfirmationDescription: {
        defaultMessage:
            'This will permanently remove the shared link. If this item is embedded on other sites it will also become inaccessible. Any custom properties, settings and expirations will be removed as well. Do you want to continue?',
        description: 'Description for confirmation modal to remove a shared link',
        id: 'boxui.share.removeLinkConfirmationDescription',
    },
    sharedLinkExpirationTooltip: {
        defaultMessage: 'This link will expire on {expiration, date, long}',
        description:
            'Tooltip describing when this shared link will expire. {expiration, date, long} is the formatted date',
        id: 'boxui.share.sharedLinkExpirationTooltip',
    },

    peopleWithLinkCanDownloadFolder: {
        defaultMessage: 'Anyone with the link can view this folder and download its contents.',
        description: 'Description of an open shared link for a folder with view and download permissions',
        id: 'boxui.share.peopleWithLinkCanDownloadFolder',
    },
    peopleWithLinkCanViewFolder: {
        defaultMessage: 'Anyone with the link can view this folder.',
        description: 'Description of an open shared link for a folder with view permissions',
        id: 'boxui.share.peopleWithLinkCanViewFolder',
    },
    peopleWithLinkCanEditFile: {
        defaultMessage: 'Anyone with the link can edit and download this file.',
        description:
            'Description of an open shared link for a file with edit permissions (implies view and download permissions as well)',
        id: 'boxui.share.peopleWithLinkCanEditFile',
    },
    peopleWithLinkCanDownloadFile: {
        defaultMessage: 'Anyone with the link can view and download this file.',
        description: 'Description of an open shared link for a file with view and download permissions',
        id: 'boxui.share.peopleWithLinkCanDownloadFile',
    },
    peopleWithLinkCanViewFile: {
        defaultMessage: 'Anyone with the link can view this file.',
        description: 'Description of an open shared link for a file with view permissions',
        id: 'boxui.share.peopleWithLinkCanViewFile',
    },

    peopleInSpecifiedCompanyCanDownloadFolder: {
        defaultMessage: 'Anyone in {company} with the link can view this folder and download its contents.',
        description:
            'Description of a specific company shared link for a folder with view and download permissions. {company} is the company name',
        id: 'boxui.share.peopleInSpecifiedCompanyCanDownloadFolder',
    },
    peopleInSpecifiedCompanyCanViewFolder: {
        defaultMessage: 'Anyone in {company} with the link can view this folder.',
        description:
            'Description of an specific company shared link for a folder with view permissions. {company} is the company name',
        id: 'boxui.share.peopleInSpecifiedCompanyCanViewFolder',
    },
    peopleInCompanyCanDownloadFolder: {
        defaultMessage: 'Anyone in your company with the link can view this folder and download its contents.',
        description: 'Description of a company shared link for a folder with view and download permissions',
        id: 'boxui.share.peopleInCompanyCanDownloadFolder',
    },
    peopleInCompanyCanViewFolder: {
        defaultMessage: 'Anyone in your company with the link can view this folder.',
        description: 'Description of a company shared link for a folder with view permissions',
        id: 'boxui.share.peopleInCompanyCanViewFolder',
    },

    peopleInSpecifiedCompanyCanEditFile: {
        defaultMessage: 'Anyone in {company} with the link can edit and download this file.',
        description:
            'Description of a specific company shared link for a file with edit permissions (implies view and download permissions as well). {company} is the company name',
        id: 'boxui.share.peopleInSpecifiedCompanyCanEditFile',
    },
    peopleInSpecifiedCompanyCanDownloadFile: {
        defaultMessage: 'Anyone in {company} with the link can view and download this file.',
        description:
            'Description of a specific company shared link for a file with view and download permissions. {company} is the company name',
        id: 'boxui.share.peopleInSpecifiedCompanyCanDownloadFile',
    },
    peopleInSpecifiedCompanyCanViewFile: {
        defaultMessage: 'Anyone in {company} with the link can view this file.',
        description:
            'Description of an specific company shared link for a file with view permissions. {company} is the company name',
        id: 'boxui.share.peopleInSpecifiedCompanyCanViewFile',
    },
    peopleInCompanyCanEditFile: {
        defaultMessage: 'Anyone in your company with the link can edit and download this file.',
        description:
            'Description of a company shared link for a file with edit permissions (implies view and download permissions as well)',
        id: 'boxui.share.peopleInCompanyCanEditFile',
    },
    peopleInCompanyCanDownloadFile: {
        defaultMessage: 'Anyone in your company with the link can view and download this file.',
        description: 'Description of a company shared link for a file with view and download permissions',
        id: 'boxui.share.peopleInCompanyCanDownloadFile',
    },
    peopleInCompanyCanViewFile: {
        defaultMessage: 'Anyone in your company with the link can view this file.',
        description: 'Description of a company shared link for a file with view permissions',
        id: 'boxui.share.peopleInCompanyCanViewFile',
    },

    peopleInItemCanPreviewAndDownloadFolder: {
        defaultMessage: 'Any collaborator on this folder with the link can view this folder and download its contents.',
        description: 'Description of a collaborator-only shared link for a folder with view and download permissions',
        id: 'boxui.share.peopleInItemCanPreviewAndDownloadFolder',
    },
    peopleInItemCanPreviewFolder: {
        defaultMessage: 'Any collaborator on this folder with the link can view this folder.',
        description: 'Description of a collaborator-only shared link for a folder with view permissions',
        id: 'boxui.share.peopleInItemCanPreviewFolder',
    },
    peopleInItemCanDownloadFolder: {
        defaultMessage: 'Any collaborator on this folder with the link can download this folder.',
        description: 'Description of a collaborator-only shared link for a folder with download permissions',
        id: 'boxui.share.peopleInItemCanDownloadFolder',
    },
    peopleInItemCanAccessFolder: {
        defaultMessage: 'Any collaborator on the folder with the link can access this folder.',
        description: 'Description of a collaborator-only shared link for a folder with no permissions',
        id: 'boxui.share.peopleInItemCanAccessFolder',
    },

    peopleInItemCanEditFile: {
        defaultMessage: 'Any collaborator on this file with the link can edit this file and download its contents.',
        description:
            'Description of a collaborator-only shared link for a file with edit permissions (implies view and download permissions as well)',
        id: 'boxui.share.peopleInItemCanEditFile',
    },
    peopleInItemCanPreviewAndDownloadFile: {
        defaultMessage: 'Any collaborator on this file with the link can view this file and download its contents.',
        description: 'Description of a collaborator-only shared link for a file with view and download permissions',
        id: 'boxui.share.peopleInItemCanPreviewAndDownloadFile',
    },
    peopleInItemCanPreviewFile: {
        defaultMessage: 'Any collaborator on this file with the link can view this file.',
        description: 'Description of a collaborator-only shared link for a file with view permissions',
        id: 'boxui.share.peopleInItemCanPreviewFile',
    },
    peopleInItemCanDownloadFile: {
        defaultMessage: 'Any collaborator on this file with the link can download this file.',
        description: 'Description of a collaborator-only shared link for a file with download permissions',
        id: 'boxui.share.peopleInItemCanDownloadFile',
    },
    peopleInItemCanAccessFile: {
        defaultMessage: 'Any collaborator on the file with the link can access this file.',
        description: 'Description of a collaborator-only shared link for a file with no permissions',
        id: 'boxui.share.peopleInItemCanAccessFile',
    },
});

export default messages;
